import React, { FunctionComponent, useCallback, useState, useEffect } from 'react';

import classnames from 'classnames';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';
import { ValueType } from 'react-select';

import { MetaData } from 'common/response-editor/MetadataParser';
import { MediaType, UploadedFileAsset } from 'common/upload/Upload';
import { Overlay } from 'core/overlay/Overlay';
import { Select } from 'core/select/Select';
import { ButtonVariant, Size } from 'core/util/Bootstrap';
import { Filter, SearchRequest } from 'core/util/Search';
import { Collage, CollageGallery } from 'social/common/collage/Collage';
import { formatLinkForAssetCardDisplay } from 'social/common/util/Util';
import { Asset, AssetType, LabelMetaDatum } from 'social/publisher/Publisher.types';
import { usePublisherDispatch } from 'social/publisher/PublisherContext';
import { fetchAssets, fetchAssetTags, fetchAssetCount } from 'social/publisher/PublisherFetchers';

import 'social/publisher/asset-selector/AssetSelector.scss';

interface Props {
    tenantId: number | undefined;
    responseEditorRef: any;
    content: MetaData[];
    media: any[];
    isAssetsLocked: boolean;
}

type OptionType = {
    value: string;
    label: string;
};

export const AssetSelector: FunctionComponent<Props> = ({
    tenantId,
    responseEditorRef,
    content,
    media,
    isAssetsLocked,
}) => {
    const dispatch = usePublisherDispatch();
    const { t } = useTranslation(['social/publisher']);
    const PAGINATION_COUNT = 42;
    const [assets, setAssets] = useState<Asset[]>([]);
    const [tagOptions, setTagOptions] = useState<ValueType<OptionType>>([]);
    const [isShowAssetSelector, setIsShowAssetSelector] = useState(false);
    // will support multi-select in the future
    const [selectedTagOptions, setSelectedTagOptions] = useState<ValueType<OptionType>>([]);
    const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
    const [addAssetError, setAddAssetError] = useState('');
    const [hasMoreAssets, setHasMoreAssets] = useState(true);
    const [paginationStart, setPaginationStart] = useState(0);
    const [isPromiseInProgress, setIsPromiseInProgress] = useState(false);
    const [assetCount, setAssetCount] = useState(0);
    // prevent making search asset and asset count call every time a tag is selected
    const [isSelectingTags, setIsSelectingTags] = useState(false);
    const isNewAsset = (asset: Asset): boolean => {
        const createDate = DateTime.fromISO(asset.baseDomainMetaDatum.dateCreatedOn as string);
        const now = DateTime.local();

        return createDate > now.minus({ day: 2 });
    };
    const closeAndReset = (): void => {
        // Do not change the order
        setIsShowAssetSelector(false);
        setSelectedTagOptions([]);
        setSelectedAssetIds([]);
        setAddAssetError('');
        setPaginationStart(0);
        setHasMoreAssets(true);
    };
    const handleCloseModal = (): void => {
        closeAndReset();
    };
    const handleOpenModal = (): void => {
        setIsShowAssetSelector(true);
    };
    const handleSelectTags = (selectedOptions: ValueType<OptionType>): void => {
        setSelectedTagOptions(selectedOptions);
        setSelectedAssetIds([]);
        setAddAssetError('');
        setPaginationStart(0);
        setHasMoreAssets(true);
    };
    const handleSelectAsset = (assetId: string): void => {
        let newSelectedAssetIds;

        if (selectedAssetIds.indexOf(assetId) > -1) {
            newSelectedAssetIds = selectedAssetIds.filter((el) => el !== assetId);
        } else {
            newSelectedAssetIds = [...selectedAssetIds, assetId];
        }
        setSelectedAssetIds(newSelectedAssetIds);
    };
    const handleDeselectAllAssets = (): void => {
        setSelectedAssetIds([]);
        setAddAssetError('');
    };
    const handleAddAssets = (): void => {
        let labelIds: any[] = [];
        const selectedAssets = assets.filter((el) => selectedAssetIds.indexOf(el.id) > -1);

        if (selectedAssets.length > 10) {
            setAddAssetError('ASSET_SELECT_LIMIT');
            return;
        }
        const textAssets = selectedAssets.filter((el) => el.assetType === AssetType.TEXT);
        const linkAssets = selectedAssets.filter((el) => el.assetType === AssetType.LINK);
        const mediaAssets = selectedAssets.filter(
            (el) => el.assetType === AssetType.PICTURE || el.assetType === AssetType.VIDEO,
        );

        if (linkAssets.length > 1) {
            setAddAssetError('ASSET_SELECT_LINK_LIMIT');
            return;
        }
        if (linkAssets.length > 0 && mediaAssets.length > 0) {
            setAddAssetError('ASSET_SELECT_LINK_AND_MEDIA_LIMIT');
            return;
        }

        // todo: store campaignId
        const textMetaData: MetaData[] = textAssets.map((el) => {
            labelIds = labelIds.concat(el.labelIds);
            return {
                text: `${el.contentDescription}\n`,
                tags: [],
                attributes: {},
                // attributes: { data: { campaignId: el.campaignId } },
            };
        });
        // todo: store campaignId
        const linkMetaData: MetaData[] = linkAssets.map((el) => {
            labelIds = labelIds.concat(el.labelIds);
            return {
                text: el.contentDescription,
                tags: ['link'],
                attributes: { link: el.linkUrl },
                // attributes: { link: el.linkUrl, data: { campaignId: el.campaignId } },
            };
        });
        // todo: store campaignId
        const mediaFiles: UploadedFileAsset[] = mediaAssets.map((el) => {
            let fileType;
            let isImage;
            let isVideo;
            const size = _.get(el, 'medium[0].bytes');
            const fileExtension = el.mediaUrl.split('.').pop();

            labelIds = labelIds.concat(el.labelIds);

            if (el.assetType === AssetType.PICTURE) {
                fileType = `image/${fileExtension}`;
                isImage = true;
                isVideo = false;
            } else {
                fileType = `video/${fileExtension}`;
                isImage = false;
                isVideo = true;
            }

            return {
                id: el.id,
                url: el.mediaUrl,
                name: `asset-${el.id}`,
                // name: `asset-${el.id}-campaign-${el.campaignId}`,
                type: fileType,
                isVideo,
                isImage,
                size: size || 20000, // We'll honor the size if we can extract one, but we need to pass a number if we can't
            };
        });
        const newContent = [...content, ...textMetaData, ...linkMetaData];
        const newMedia = [...media, ...mediaFiles];

        dispatch({ type: 'set-content', payload: { value: newContent } });
        dispatch({ type: 'set-media', payload: { value: newMedia } });
        dispatch({ type: 'add-labelIds', payload: { value: labelIds } });

        if (responseEditorRef && responseEditorRef.current) {
            if (responseEditorRef.current.setMedia) {
                responseEditorRef.current.setMedia(newMedia);
            }
            if (responseEditorRef.current.setContents) {
                responseEditorRef.current.setContents(newContent);
            }
        }
        closeAndReset();
    };
    const buildAssetCard = (asset: Asset): JSX.Element => {
        let card;
        let collageData: CollageGallery[] = [];
        const { assetType, id, locked, unlockStartDate } = asset;
        const isNew = isNewAsset(asset);
        const isSelected = selectedAssetIds.indexOf(id) > -1;
        const assetCardClass = classnames(
            'asset-card',
            { [`asset-${AssetType[assetType].toLowerCase()}`]: true },
            { selected: isSelected },
            { locked },
        );
        const checkbox = (
            <div
                className={classnames('asset-toggle-btn', {
                    active: isSelected,
                })}
                role="button"
                onClick={(): void => handleSelectAsset(id)}
                onKeyDown={(): void => handleSelectAsset(id)}
                tabIndex={0}
            >
                {isSelected && <div className="checkmark" />}
            </div>
        );
        const newAssetLabel = (
            <div className="asset-new-label">
                <span>{t('NEW')}</span>
                <div id="triangle-top" />
                <div id="triangle-bottom" />
            </div>
        );
        const lockedIcon = (
            <div className="asset-locked-icon">
                <i className="icon icon-lock" />
            </div>
        );
        const unlockedMessage = (
            <div className="asset-locked-message">
                <span>
                    {t('ASSET_UNLOCK_DATE', {
                        date: DateTime.fromISO(unlockStartDate!).toLocaleString(DateTime.DATE_FULL),
                    })}
                </span>
            </div>
        );

        switch (assetType) {
            case AssetType.TEXT:
                card = (
                    <div key={id} className={assetCardClass}>
                        <div className="asset-card-content">
                            <span>{asset.contentDescription}</span>
                        </div>
                        <div className="asset-card-overlay">
                            {!locked && checkbox}
                            {isNew && newAssetLabel}
                            {locked && lockedIcon}
                            {locked && unlockStartDate && unlockedMessage}
                        </div>
                    </div>
                );
                break;
            case AssetType.LINK:
                collageData = [{ url: asset.mediaUrl, type: MediaType.Image }];
                card = (
                    <div key={id} className={assetCardClass}>
                        <div className="asset-card-content">
                            <div
                                className={classnames(
                                    'preview-link',
                                    { 'with-image': asset.mediaUrl },
                                    { 'without-image': !asset.mediaUrl },
                                )}
                            >
                                {asset.mediaUrl && (
                                    <div className="link-image">
                                        <Collage galleryData={collageData} maxCount={1} openModalOnMediaClick={false} />
                                    </div>
                                )}
                                <div className="link-info">
                                    <div className="link-title">{asset.contentTitle}</div>
                                    <div className="link-description">{asset.contentDescription}</div>
                                    {asset.linkUrl && (
                                        <div className="link-url">{formatLinkForAssetCardDisplay(asset.linkUrl)}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="asset-card-overlay">
                            {!locked && checkbox}
                            {isNew && newAssetLabel}
                            {locked && lockedIcon}
                            {locked && unlockStartDate && unlockedMessage}
                        </div>
                    </div>
                );
                break;
            case AssetType.PICTURE:
                collageData = [
                    {
                        type: MediaType.Image,
                        name: asset.contentDescription,
                        url: asset.mediaUrl,
                    },
                ];
                card = (
                    <div key={id} className={assetCardClass}>
                        <div className="asset-card-content">
                            {asset.mediaUrl && (
                                <Collage galleryData={collageData} maxCount={1} openModalOnMediaClick={false} />
                            )}
                        </div>
                        <div className="asset-card-overlay">
                            {!locked && checkbox}
                            {isNew && newAssetLabel}
                            {locked && lockedIcon}
                            {locked && unlockStartDate && unlockedMessage}
                        </div>
                    </div>
                );
                break;
            case AssetType.VIDEO:
                collageData = [
                    {
                        type: MediaType.Video,
                        name: asset.contentDescription,
                        url: asset.mediaUrl,
                    },
                ];
                card = (
                    <div key={id} className={assetCardClass}>
                        <div className="asset-card-content">
                            {asset.mediaUrl && (
                                <Collage galleryData={collageData} maxCount={1} openModalOnMediaClick={false} />
                            )}
                        </div>
                        <div className="asset-card-overlay">
                            {!locked && checkbox}
                            {isNew && newAssetLabel}
                            {locked && lockedIcon}
                            {locked && unlockStartDate && unlockedMessage}
                        </div>
                    </div>
                );
                break;
            default:
                card = (
                    <div key={id} className="asset-card asset-unsupported">
                        <span>{t('ASSET_TYPE_NOT_SUPPORTED')}</span>
                    </div>
                );
        }
        return card;
    };

    const selectedTagOptionsString = JSON.stringify(selectedTagOptions);
    const loadAssets = useCallback(async () => {
        if (paginationStart === 0) {
            setAssets([]);
        }

        const filters: Filter[] = [
            {
                name: '__scr_asset_archived__',
                values: ['false'],
            },
        ];
        let selectedTagIds: string[] = [];
        // could be null or undefined
        const selectedTagOptionsArray = JSON.parse(selectedTagOptionsString);

        if (selectedTagOptionsArray && selectedTagOptionsArray.length > 0) {
            selectedTagIds = _.map(selectedTagOptionsArray, 'value');
        }

        if (selectedTagIds.length > 0) {
            filters.push({
                name: '__scr_asset_label_ids__',
                values: selectedTagIds,
            });
        }

        const search: SearchRequest = {
            start: paginationStart,
            filters,
            count: PAGINATION_COUNT,
            sorts: [
                { name: '__scr_asset_created_date__', sortOrder: 0 },
                { name: '__scr_asset_locked__', sortOrder: 1 },
            ],
        };

        setIsPromiseInProgress(true);

        const newAssets: Asset[] = await trackPromise(fetchAssets(tenantId, search), 'assets');

        setIsPromiseInProgress(false);

        if (paginationStart === 0) {
            setAssets(newAssets);
        } else {
            setAssets((previousAssets) => [...previousAssets, ...newAssets]);
        }

        if (newAssets.length < PAGINATION_COUNT) {
            setHasMoreAssets(false);
        }
    }, [tenantId, paginationStart, selectedTagOptionsString]);

    const loadTags = useCallback(async () => {
        const tags: LabelMetaDatum[] = await trackPromise(fetchAssetTags(tenantId), 'tags');
        const tagOptions = tags.map((el) => {
            return {
                label: `${el.label.name} (${el.count})`,
                value: `${el.label.id}`,
            };
        });

        setTagOptions(tagOptions);
    }, [tenantId]);

    const loadAssetCount = useCallback(async () => {
        const search: SearchRequest = {
            filters: [
                {
                    name: '__scr_asset_archived__',
                    values: ['false'],
                },
            ],
        };
        let selectedTagIds: string[] = [];
        // could be null or undefined
        const selectedTagOptionsArray = JSON.parse(selectedTagOptionsString);

        if (selectedTagOptionsArray && selectedTagOptionsArray.length > 0) {
            selectedTagIds = _.map(selectedTagOptionsArray, 'value');
        }

        if (selectedTagIds.length > 0) {
            search.filters.push({
                name: '__scr_asset_label_ids__',
                values: selectedTagIds,
            });
        }

        const count = await fetchAssetCount(tenantId, search);

        setAssetCount(count);
    }, [tenantId, selectedTagOptionsString]);

    useEffect(() => {
        if (isShowAssetSelector && !isSelectingTags) {
            loadAssets();
        }
    }, [loadAssets, isShowAssetSelector, isSelectingTags]);

    useEffect(() => {
        if (isShowAssetSelector) {
            loadTags();
        }
    }, [loadTags, isShowAssetSelector]);

    useEffect(() => {
        if (isShowAssetSelector && !isSelectingTags) {
            loadAssetCount();
        }
    }, [loadAssetCount, isShowAssetSelector, isSelectingTags]);

    const handleInfiniteScroll = (e: React.UIEvent<HTMLElement>): void => {
        const eventTarget = e.target as HTMLElement;
        const isScrollToBottom = eventTarget.scrollTop + eventTarget.clientHeight >= eventTarget.scrollHeight - 1;

        if (isScrollToBottom && hasMoreAssets && !isPromiseInProgress) {
            setPaginationStart(paginationStart + PAGINATION_COUNT);
        }
    };

    return (
        <div className="r4e-social-asset-selector">
            <Button
                className={classnames('r4e-social-asset-selector-btn', { disabled: isAssetsLocked })}
                onClick={(): void => {
                    if (isAssetsLocked) {
                        return;
                    }
                    handleOpenModal();
                }}
                variant="link"
            >
                {t('SELECT_FROM_ASSET_LIBRARY')}
            </Button>
            <Modal
                show={isShowAssetSelector}
                onHide={handleCloseModal}
                className="r4e-social-asset-selector-modal"
                size={Size.XLarge}
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('ASSET_SELECTOR_TITLE')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Overlay area="assets" />
                    <div className="asset-search">
                        <Overlay area="tags" />
                        <Select
                            name="asset-tags"
                            className="r4e-select"
                            classNamePrefix="r4e-select"
                            options={tagOptions}
                            value={selectedTagOptions}
                            onChange={handleSelectTags}
                            onMenuOpen={(): void => setIsSelectingTags(true)}
                            onMenuClose={(): void => setIsSelectingTags(false)}
                            isMulti
                            placeholder={t('SEARCH_BY_TAGS')}
                        />
                    </div>
                    <div className="asset-header">
                        <h5 className="asset-count">{t('ASSET_COUNT', { count: assetCount })}</h5>
                        <h6>{t('IMAGES_VIDEOS_MESSAGES')}</h6>
                    </div>
                    <div className="asset-options" onScroll={handleInfiniteScroll}>
                        {assets.map(buildAssetCard)}
                        {assets.length === 0 && <div className="no-assets-message">{t('NO_ASSETS_MESSAGE')}</div>}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="selected-asset-count">
                        <span>{t('SELECTED_ASSET_COUNT', { count: selectedAssetIds.length })}</span>
                    </div>
                    <div className="add-asset-validation">
                        {addAssetError && (
                            <div className="add-asset-error">
                                <div className="error-icon">
                                    <i className="icon icon-exclamation-circle" />
                                </div>
                                <div className="error-message">
                                    <span>{t(addAssetError)}</span>
                                </div>
                                <div className="deselect-btn">
                                    <Button variant={ButtonVariant.Link} onClick={handleDeselectAllAssets}>
                                        {t('DESELECT_ALL')}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="action-btns">
                        <Button variant={ButtonVariant.Default} onClick={handleCloseModal}>
                            {t('CANCEL')}
                        </Button>
                        <Button
                            variant={ButtonVariant.Primary}
                            onClick={handleAddAssets}
                            disabled={selectedAssetIds.length < 1}
                        >
                            {t('ADD')}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
